import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Capacitor } from "@capacitor/core";
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useShift } from '@/hooks/useShift';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { usePatientRole } from '@/hooks/usePatientRole';
import { PATIENT_INACTIVE_TOAST_MESSAGE } from '@/lib/patient-status-errors';
import { usePatientCompany } from '@/hooks/usePatientCompany';
import { sanitizePatientData } from '@/hooks/useSensitivePatientData';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/hooks/useCompany';
import { AssignCompanyCaregiverDialog } from '@/components/patient/AssignCompanyCaregiverDialog';
import { ArchivePatientDialog } from '@/components/patient/ArchivePatientDialog';

import { extractPathFromUrl } from '@/lib/storage-utils';
import { track } from '@/lib/analytics';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ShiftTemplatesList } from '@/components/shift-templates/ShiftTemplatesList';
import { CoachMark } from '@/components/onboarding/CoachMark';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import { CaregiverShiftAssignment } from '@/components/team/CaregiverShiftAssignment';
import { CareConfigDelegationToggle } from '@/components/team/CareConfigDelegationToggle';
import { PatientDetailsCard } from '@/components/patient/PatientDetailsCard';
import {
  MapPin,
  LogOut,
  Users,
  UserPlus,
  ClipboardList,
  Copy,
  Check,
  Calendar,
  BarChart3,
  Package,
  Eye,
  MessageCircle,
  Trash2,
  Activity,
  Archive,
  ArchiveRestore
} from 'lucide-react';
import { toast } from 'sonner';
import type { Tables } from '@/integrations/supabase/types';

type Patient = Tables<'patients'>;
type InventoryItem = Tables<'inventory'>;
type PatientLink = Tables<'patient_user_links'> & {
  active?: boolean;
};

interface Member extends PatientLink {
  profile?: {
    full_name: string | null;
    avatar_url: string | null;
  };
  email?: string;
  signedUrl?: string | null;
}

interface InviteLink {
  link: string;
  role: 'caregiver' | 'observer';
  copied: boolean;
}

export default function PatientDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { setFocusedPatientId } = useFocusedPatient();
  const { forceCheckOut, activeShift, isLoading: localShiftLoading } = useShift();
  const queryClient = useQueryClient();
  const { isCompanyMember } = useCompany();
  const [isForceCheckingOut, setIsForceCheckingOut] = useState(false);
  const [showForceCheckOutDialog, setShowForceCheckOutDialog] = useState(false);

  // Align focused patient context so inventory-alerts and other global state are shared
  useEffect(() => {
    if (id) {
      setFocusedPatientId(id);
    }
  }, [id, setFocusedPatientId]);

  const {
    isOwner,
    isObserver,
    isCaregiver,
    canEdit: canEditRole,
    canInvite: canInviteRole,
    canManageTeam: canManageTeamRole,
    canManageSchedules: canManageSchedulesRole,
    canAssignShiftTemplates: canAssignShiftTemplatesRole,
    isCareConfigDelegate,
    canExecuteTasks,
    canViewDashboard,
    isPatientActive,
    isLoading: roleLoading
  } = usePatientRole(id);

  // Gate write actions when patient is suspended/archived (RLS will also block, but UI should reflect it)
  const canEdit = canEditRole && isPatientActive;
  const canInvite = canInviteRole && isPatientActive;
  const canManageTeam = canManageTeamRole && isPatientActive;
  const canManageSchedules = canManageSchedulesRole && isPatientActive;
  const canAssignShiftTemplates = canAssignShiftTemplatesRole && isPatientActive;

  const { isCompanyPatient, availableCaregivers, refetchCaregivers } = usePatientCompany(id);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false);

  const [inviteLinks, setInviteLinks] = useState<InviteLink[]>([]);
  const [removeMemberDialog, setRemoveMemberDialog] = useState<{
    member: Member;
    memberActiveShift: { id: string } | null;
    isSelf: boolean;
    title: string;
    description: string;
  } | null>(null);
  const [isRemovingMember, setIsRemovingMember] = useState(false);

  // Check if ANYONE has an active shift for this patient (not just current user)
  const { data: globalActiveShift, isLoading: globalShiftLoading } = useQuery({
    queryKey: ['global-active-shift', id],
    queryFn: async () => {
      if (!id) return null;

      // 1. Fetch the active shift
      const { data: shift, error } = await supabase
        .from('shifts')
        .select(`
          id, 
          check_in_at, 
          caregiver_id, 
          check_in_latitude, 
          check_in_longitude
        `)
        .eq('patient_id', id)
        .eq('status', 'active')
        .maybeSingle();

      if (error) throw error;
      if (!shift) return null;

      // 2. Fetch caregiver name + phone separately to avoid complex joins/RLS issues
      let caregiverName = null;
      let caregiverPhone = null;
      if (shift.caregiver_id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, phone')
          .eq('user_id', shift.caregiver_id)
          .single();

        if (profile) {
          caregiverName = profile.full_name;
          caregiverPhone = profile.phone;
        }
      }

      // Transform to match ActiveShift interface
      return {
        id: shift.id,
        check_in_at: shift.check_in_at,
        caregiver_id: shift.caregiver_id,
        caregiver_name: caregiverName,
        caregiver_phone: caregiverPhone,
        check_in_latitude: shift.check_in_latitude,
        check_in_longitude: shift.check_in_longitude
      };
    },
    enabled: !!id,
    // Realtime listener below invalidates this query; keep a slow safety poll.
    refetchInterval: 60000,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  // Realtime: invalidate active-shift query when any shift for this patient changes.
  useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`patient-active-shift-${id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'shifts', filter: `patient_id=eq.${id}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['global-active-shift', id] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, queryClient]);

  // Combine local active shift (if I am the one) with global
  const effectiveActiveShift = activeShift?.patient_id === id ? activeShift : globalActiveShift;

  const openForceCheckOutDialog = () => {
    if (!id || !effectiveActiveShift) return;
    setShowForceCheckOutDialog(true);
  };

  const handleForceCheckOut = async () => {
    if (!id || !effectiveActiveShift) return;
    setShowForceCheckOutDialog(false);
    setIsForceCheckingOut(true);

    try {
      const adminName = members.find(m => m.user_id === user?.id)?.profile?.full_name || user?.email || 'Administrador';
      // Use hardcoded Portuguese message since this is persisted in the database
      const adminNote = `Plantão encerrado pelo administrador: ${adminName}`;
      const success = await forceCheckOut(effectiveActiveShift.id, id, adminNote);
      if (success) {
        toast.success(t('handoff.shiftCompleted'));
        // Invalidate all shift-related queries to refresh the UI
        await queryClient.invalidateQueries({ queryKey: ['global-active-shift', id] });
        await queryClient.invalidateQueries({ queryKey: ['activeShift'] });
      } else {
        toast.error(t('common.error'));
      }
    } catch (error) {
      console.error('Error in force checkout UI:', error);
      toast.error(t('common.error'));
    } finally {
      setIsForceCheckingOut(false);
    }
  };

  const handleUnarchive = async () => {
    if (!id || !patient) return;
    setShowUnarchiveDialog(false);
    setIsUnarchiving(true);
    try {
      const archivedAt = (patient as any).archived_at as string | null;
      const daysArchived = archivedAt
        ? Math.floor((Date.now() - new Date(archivedAt).getTime()) / 86400000)
        : 0;

      const { error } = await supabase
        .from('patients')
        .update({ status: 'active' } as any)
        .eq('id', id);

      if (error) throw error;

      track('patient_unarchived', { patient_id: id, days_archived: daysArchived });
      toast.success(t('patient.unarchive.success', { defaultValue: 'Paciente reativado' }));
      await queryClient.invalidateQueries();
    } catch (e: any) {
      console.error('Error unarchiving patient:', e);
      const msg = e?.message?.includes('row-level security') || e?.code === '42501'
        ? t('patient.unarchive.noPermission', { defaultValue: 'Você não tem permissão para desarquivar este paciente.' })
        : t('common.error');
      toast.error(msg);
    } finally {
      setIsUnarchiving(false);
    }
  };

  // Fetch patient data
  const { data: patient, isLoading: patientLoading } = useQuery({
    queryKey: ['patient', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as Patient;
    },
    enabled: !!id
  });

  // Check if ANYONE has an active shift for this patient (not just current user)




  // Fetch members with profiles and batch fetch signed URLs for avatars
  const { data: members = [], isLoading: membersLoading, refetch: refetchMembers } = useQuery({
    queryKey: ['patient-members', id],
    queryFn: async () => {
      if (!id) return [];

      // Parallel fetch: Profiles and Links (Not possible parallely structure-wise as we need userIds from links)
      // But we can optimize the profile fetch + signedUrl fetch.

      // 1. Get Links
      const { data: links, error: linksError } = await supabase
        .from('patient_user_links')
        .select('*')
        .eq('patient_id', id)
        .eq('active', true);

      if (linksError) throw linksError;
      if (!links || links.length === 0) return [];

      const userIds = links.map(l => l.user_id);

      // 2. Get Profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .in('user_id', userIds);

      if (profilesError) console.error('Error fetching member profiles:', profilesError);

      // 3. Prepare Avatar Paths and Map
      const profilesMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
      const avatarPaths: string[] = [];

      profiles?.forEach(p => {
        if (p.avatar_url) {
          const path = extractPathFromUrl(p.avatar_url, 'avatars');
          if (path) avatarPaths.push(path);
        }
      });

      // 4. Fetch Signed URLs (if any)
      const signedUrlsMap = new Map<string, string>();
      if (avatarPaths.length > 0) {
        const { data: signedUrls, error: signedUrlsError } = await supabase.storage
          .from('avatars')
          .createSignedUrls(avatarPaths, 3600);

        if (!signedUrlsError && signedUrls) {
          signedUrls.forEach(item => {
            if (item.path && item.signedUrl) {
              signedUrlsMap.set(item.path, item.signedUrl);
            }
          });
        }
      }

      // 5. Construct Result
      return links.map((link: any) => {
        const profile = profilesMap.get(link.user_id);
        const avatarPath = profile?.avatar_url ? extractPathFromUrl(profile.avatar_url, 'avatars') : null;
        const signedUrl = avatarPath ? signedUrlsMap.get(avatarPath) : null;

        return {
          ...link,
          profile: profile || undefined,
          signedUrl: signedUrl,
          email: link.user_id === user?.id ? user?.email : undefined
        };
      }) as Member[];
    },
    enabled: !!id && !!user
  });



  const { data: inventoryAlerts = [] } = useQuery({
    queryKey: ['inventory-alerts', id],
    queryFn: async () => {
      if (!id) return [];
      const { data, error } = await supabase
        .from('inventory')
        .select('id')
        .eq('patient_id', id)
        .neq('status', 'ok');
      if (error) throw error;
      return data || [];
    },
    enabled: !!id
  });



  const handleRemoveMember = async (member: Member) => {
    const memberName = member.profile?.full_name || t('common.user');
    const isSelf = member.user_id === user?.id;

    let memberActiveShift: { id: string } | null = null;
    if (member.role === 'caregiver' && id) {
      const { data: shift } = await supabase
        .from('shifts')
        .select('id')
        .eq('patient_id', id)
        .eq('caregiver_id', member.user_id)
        .eq('status', 'active')
        .maybeSingle();

      memberActiveShift = shift;
    }

    // Block self-removal if caregiver has active shift
    if (isSelf && memberActiveShift) {
      toast.error(t('patient.mustEndShiftBeforeLeaving', {
        defaultValue: 'Você precisa encerrar seu plantão antes de sair da equipe.'
      }));
      return;
    }

    const title = isSelf
      ? t('patient.leaveTeam', { defaultValue: 'Sair da equipe' })
      : t('common.remove', { defaultValue: 'Remover' });

    const description = memberActiveShift
      ? t('patient.confirmRemoveCaregiverWithShift', { name: memberName })
      : isSelf
        ? t('patient.confirmLeaveTeam', { patient: patient?.nickname, defaultValue: `Tem certeza que deseja sair da equipe de ${patient?.nickname}?` })
        : t('patient.confirmRemoveMember', { name: memberName, defaultValue: `Tem certeza que deseja remover ${memberName} da equipe?` });

    setRemoveMemberDialog({ member, memberActiveShift, isSelf, title, description });
  };

  const confirmRemoveMember = async () => {
    if (!removeMemberDialog) return;
    const { member, memberActiveShift } = removeMemberDialog;
    setIsRemovingMember(true);

    try {
      if (memberActiveShift) {
        const { error: shiftError } = await supabase
          .from('shifts')
          .update({
            status: 'completed',
            check_out_at: new Date().toISOString(),
            notes: t('patient.shiftClosedDueToRemoval')
          })
          .eq('id', memberActiveShift.id);

        if (shiftError) {
          console.error('Error closing shift:', shiftError);
          toast.error(t('patient.errorClosingShift'));
          return;
        }
      }

      const { error } = await supabase
        .from('patient_user_links')
        .update({ active: false } as any)
        .eq('id', member.id);

      if (error) throw error;

      if (member.user_id === user?.id) {
        const { data: ownerLink } = await supabase
          .from('patient_user_links')
          .select('user_id')
          .eq('patient_id', id)
          .eq('role', 'owner')
          .maybeSingle();

        if (ownerLink) {
          await supabase.from('notifications').insert({
            user_id: ownerLink.user_id,
            patient_id: id,
            type: 'team_update',
            title: t('notifications.teamUpdateTitle'),
            message: t('notifications.memberLeftMessage', {
              name: member.profile?.full_name || user?.email,
              patient: patient?.nickname
            })
          });
        }

        toast.success(
          memberActiveShift
            ? t('patient.memberRemovedAndShiftClosed')
            : t('patient.memberRemoved')
        );

        setFocusedPatientId(null);
        await queryClient.invalidateQueries({ queryKey: ['home-patients'] });
        await queryClient.invalidateQueries({ queryKey: ['patients'] });
        await queryClient.invalidateQueries({ queryKey: ['patient-role', id] });
        await queryClient.invalidateQueries({ queryKey: ['patient-members', id] });
        setRemoveMemberDialog(null);
        navigate('/app');
        return;
      }

      toast.success(
        memberActiveShift
          ? t('patient.memberRemovedAndShiftClosed')
          : t('patient.memberRemoved')
      );
      refetchMembers();
      setRemoveMemberDialog(null);
    } catch (error: any) {
      console.error('Error removing member:', error);
      toast.error(t('common.error'));
    } finally {
      setIsRemovingMember(false);
    }
  };

  const generateInviteLink = async (role: 'caregiver' | 'observer') => {
    if (!id || !user) return;
    if (!isPatientActive) {
      toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
      return;
    }

    try {
      const { data, error } = await supabase
        .rpc('create_patient_invitation', {
          p_patient_id: id,
          p_role: role,
        })
        .single();

      if (error) throw error;

      const baseUrl = Capacitor.isNativePlatform() ? 'https://careos.life' : window.location.origin;
      const link = `${baseUrl}/invite/${(data as any).token}`;

      setInviteLinks(prev => [...prev, { link, role, copied: false }]);

      track('caregiver_invited', {
        patient_id: id,
        role,
        channel: 'link',
      });

      await navigator.clipboard.writeText(link);
      toast.success(t('patient.inviteCopied'));

      setInviteLinks(prev =>
        prev.map(inv =>
          inv.link === link ? { ...inv, copied: true } : inv
        )
      );

      setTimeout(() => {
        setInviteLinks(prev =>
          prev.map(inv =>
            inv.link === link ? { ...inv, copied: false } : inv
          )
        );
      }, 3000);
    } catch (error: any) {
      console.error('Error generating invite:', error);
      toast.error(t('common.error'));
    }
  };

  const copyInviteLink = async (link: string) => {
    await navigator.clipboard.writeText(link);
    toast.success(t('patient.inviteCopied'));

    setInviteLinks(prev =>
      prev.map(inv =>
        inv.link === link ? { ...inv, copied: true } : inv
      )
    );

    setTimeout(() => {
      setInviteLinks(prev =>
        prev.map(inv =>
          inv.link === link ? { ...inv, copied: false } : inv
        )
      );
    }, 3000);
  };

  const shareViaWhatsApp = (link: string, role: 'caregiver' | 'observer') => {
    const roleLabel = role === 'caregiver' ? t('roles.caregiver') : t('roles.observer');
    const patientName = patient?.nickname || '';
    const message = t('patient.whatsappInviteMessage', {
      role: roleLabel,
      patient: patientName,
      link
    });
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Fetch company member user_ids among owners to show "Gestor" label
  // Caregiver shift coverage summary
  const caregiverMembers = members.filter(m => m.role === 'caregiver');
  const caregiversWithoutShift = caregiverMembers.filter(m => !m.shift_template_id).length;
  const shiftAssignmentCounts = caregiverMembers.reduce<Record<string, number>>((acc, m) => {
    if (m.shift_template_id) acc[m.shift_template_id] = (acc[m.shift_template_id] || 0) + 1;
    return acc;
  }, {});

  const ownerUserIds = members.filter(m => m.role === 'owner').map(m => m.user_id);
  const { data: companyMemberIds = [] } = useQuery({
    queryKey: ['company-member-check', ownerUserIds],
    queryFn: async () => {
      if (ownerUserIds.length === 0) return [];
      const { data } = await supabase
        .from('company_members')
        .select('user_id')
        .in('user_id', ownerUserIds)
        .eq('is_active', true);
      return (data || []).map(d => d.user_id);
    },
    enabled: ownerUserIds.length > 0
  });

  const getRoleLabel = (role: string, userId?: string) => {
    switch (role) {
      case 'owner':
        // Check if this specific user is a company member
        if (userId && companyMemberIds.includes(userId)) return t('roles.manager');
        if (!userId && isCompanyMember) return t('roles.manager');
        return t('roles.owner');
      case 'caregiver': return t('roles.caregiver');
      case 'observer': return t('roles.observer');
      default: return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-primary/10 text-primary';
      case 'caregiver': return 'bg-success/10 text-success';
      case 'observer': return 'bg-secondary/10 text-secondary';
      default: return 'bg-secondary/10 text-secondary';
    }
  };

  const isCheckedIn = !!effectiveActiveShift && effectiveActiveShift.caregiver_id === user?.id; // Only I am checked in
  const canCheckIn = isCaregiver && !effectiveActiveShift && !canExecuteTasks;
  const showCheckIn = canCheckIn;
  const showCheckOut = canExecuteTasks; // Only show checkout if I CAN execute tasks (meaning I am the one active)

  const isLoading = patientLoading || membersLoading || roleLoading || globalShiftLoading || localShiftLoading;

  if (isLoading) {
    return (
      <AppLayout showFAB={false}>
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="w-10 h-10 rounded-full" />
          <Skeleton className="h-6 w-32" />
        </div>
        <Skeleton className="h-48 w-full rounded-xl" />
      </AppLayout>
    );
  }

  if (!patient) {
    return null;
  }

  return (
    <AppLayout
      title={t('patient.details')}
      showFAB={false}
      showBack
      onBack={() => navigate('/app/patients')}
    >
      <div className="p-4">
        {/* Activation checklist (only while required steps are pending) */}
        {isOwner && <OnboardingChecklist requiredOnly />}

        {/* Archived banner */}

        {(patient as any).status === 'archived' && (
          <div className="mb-4 rounded-lg border border-muted-foreground/40 bg-muted/40 p-4 flex items-center gap-3">
            <Archive className="h-4 w-4 shrink-0 text-foreground" />
            <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="text-sm">
                {t('patient.archive.banner', {
                  date: (patient as any).archived_at
                    ? new Date((patient as any).archived_at).toLocaleDateString()
                    : '',
                  defaultValue: 'Paciente arquivado. Modo somente leitura.',
                })}
              </span>
              {isOwner && (
                <Button size="sm" variant="outline" onClick={() => setShowUnarchiveDialog(true)} disabled={isUnarchiving} className="shrink-0">
                  <ArchiveRestore className="w-4 h-4 mr-2" />
                  {t('patient.unarchive.action', { defaultValue: 'Desarquivar' })}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Suspended (subscription downgrade) banner */}
        {(patient as any).status === 'suspended' && (
          <div className="mb-4 rounded-lg border border-warning/40 bg-warning/10 p-4 flex items-center gap-3">
            <Archive className="h-4 w-4 shrink-0 text-warning" />
            <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="text-sm">
                {t('patient.suspended.banner', {
                  defaultValue:
                    'Paciente suspenso por exceder o limite do seu plano. Modo somente leitura. Para reativar, adicione mais slots ou arquive outro paciente.',
                })}
              </span>
              {isOwner && (
                <Button size="sm" variant="outline" onClick={() => navigate('/app/subscription')} className="shrink-0">
                  {t('patient.suspended.manage', { defaultValue: 'Gerenciar assinatura' })}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Unarchive confirmation dialog */}
        <AlertDialog open={showUnarchiveDialog} onOpenChange={setShowUnarchiveDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('patient.unarchive.title', { defaultValue: 'Desarquivar paciente' })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('patient.unarchive.confirm', {
                  defaultValue:
                    'Desarquivar este paciente? Ele voltará a ocupar um slot da sua assinatura.',
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isUnarchiving}>
                {t('common.cancel', { defaultValue: 'Cancelar' })}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleUnarchive} disabled={isUnarchiving}>
                {isUnarchiving
                  ? t('common.loading', { defaultValue: 'Carregando...' })
                  : t('patient.unarchive.action', { defaultValue: 'Desarquivar' })}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Force checkout confirmation dialog */}
        <AlertDialog open={showForceCheckOutDialog} onOpenChange={setShowForceCheckOutDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('caregiver.forceCheckOutConfirmTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('caregiver.forceCheckOutConfirmDesc', {
                  name: effectiveActiveShift
                    ? ((effectiveActiveShift as any).caregiver_name ||
                        members.find(m => m.user_id === effectiveActiveShift.caregiver_id)?.profile?.full_name ||
                        t('common.user'))
                    : ''
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isForceCheckingOut}>
                {t('common.cancel', { defaultValue: 'Cancelar' })}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleForceCheckOut}
                disabled={isForceCheckingOut}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isForceCheckingOut
                  ? t('common.loading')
                  : t('caregiver.forceCheckOut')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>


        {/* Patient Header - Standardized Card with sensitive data filtered */}
        <div className="mb-6">
          <PatientDetailsCard
            patient={sanitizePatientData(patient, isOwner || isCaregiver || canEdit)}
            activeShift={effectiveActiveShift ? {
              ...effectiveActiveShift,
              caregiver_name: (effectiveActiveShift as any).caregiver_name ||
                members.find(m => m.user_id === effectiveActiveShift.caregiver_id)?.profile?.full_name ||
                null,
              caregiver_phone: (effectiveActiveShift as any).caregiver_phone ||
                (members.find(m => m.user_id === effectiveActiveShift.caregiver_id)?.profile as any)?.phone ||
                null
            } : null}
            onClick={() => canEdit && (patient as any).status !== 'archived' && navigate(`/app/patients/${id}/edit`)}
            showEditHint={canEdit && (patient as any).status !== 'archived'}
            showCheckIn={false}
            showCheckOut={false}
            isCheckedIn={isCheckedIn}
            onCheckIn={() => navigate(`/app/shifts/${id}/check-in`)}
            onCheckOut={() => navigate(`/app/shifts/${activeShift?.id}/handoff`)}
            showHealthDetails={isOwner || isCaregiver || canEdit}
            showNotes={isOwner || isCaregiver || canEdit}
            showLocationStatus={isOwner || canEdit}
            showCaregiverPhone={isOwner || isObserver}
            onForceCheckOut={isOwner ? openForceCheckOutDialog : undefined}
            isForceCheckingOut={isForceCheckingOut}
          />
        </div>

        {/* Quick Actions - Based on Role */}
        <div className="flex flex-col gap-3 mb-6">
          {canViewDashboard && (
            <Button
              variant="default"
              className="w-full h-auto py-4 flex items-center justify-center gap-3"
              onClick={() => navigate(`/app/family/${id}`)}
            >
              <BarChart3 className="w-6 h-6" />
              <span className="text-base font-medium">{t('patient.followPatient')}</span>
            </Button>
          )}

          {canExecuteTasks && (
            <Button
              variant={canViewDashboard ? "outline" : "default"}
              className="w-full h-auto py-4 flex items-center justify-center gap-3"
              onClick={() => navigate(`/app/caregiver/${id}`)}
            >
              <Calendar className="w-6 h-6" />
              <span className="text-base font-medium">{t('patient.caregiverMode')}</span>
            </Button>
          )}


          {isObserver && !canExecuteTasks && !canViewDashboard && (
            <Button
              variant="outline"
              className="w-full h-auto py-4 flex items-center justify-center gap-3"
              onClick={() => navigate(`/app/family/${id}`)}
            >
              <Eye className="w-6 h-6" />
              <span className="text-base font-medium">{t('patient.followPatient')}</span>
            </Button>
          )}
        </div>

        {/* Secondary Actions */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Button
            variant="outline"
            className="h-auto min-h-16 py-3 flex-col gap-1"
            onClick={() => navigate(`/app/patients/${id}/care-plan`)}
            data-tour="patient-care-plan"
          >
            <ClipboardList className="w-5 h-5 shrink-0" />
            <span className="text-xs whitespace-normal text-center leading-tight break-words">{t('patient.carePlan')}</span>
          </Button>

          <Button
            variant="outline"
            className="h-auto min-h-16 py-3 flex-col gap-1 relative"
            onClick={() => navigate(`/app/inventory/${id}`)}
            data-tour="patient-inventory"
          >
            <Package className="w-5 h-5 shrink-0" />
            <span className="text-xs whitespace-normal text-center leading-tight break-words">{canEdit ? t('nav.inventory') : t('patient.viewInventory')}</span>
            {inventoryAlerts.length > 0 && (
              <Badge variant="destructive" className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-[12px]">
                {inventoryAlerts.length}
              </Badge>
            )}
          </Button>

          <Button
            variant="outline"
            className="h-auto min-h-16 py-3 flex-col gap-1"
            onClick={() => navigate(`/app/vital-ranges/${id}`)}
          >
            <Activity className="w-5 h-5 shrink-0" />
            <span className="text-xs whitespace-normal text-center leading-tight break-words">{t('vitalRanges.title', { defaultValue: 'Limites Vitais' })}</span>
          </Button>
        </div>

        {/* Shift Templates - Owners and company managers/coordinators */}
        {
          (isOwner || canManageTeam || canManageSchedules) && id && (
            <div className="mb-6" data-tour="patient-shift-templates">
              <ShiftTemplatesList patientId={id} />
            </div>
          )
        }

        {/* Members */}
        <Card className="mb-6" data-tour="patient-team">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              {t('patient.team')} ({members.length})
            </CardTitle>
            {caregiverMembers.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {t('shiftTemplates.assignment.teamSummary', { count: caregiverMembers.length })}
                {caregiversWithoutShift > 0 && (
                  <>
                    {' · '}
                    <span className="text-warning font-medium">
                      {t('shiftTemplates.assignment.teamSummaryMissing', { count: caregiversWithoutShift })}
                    </span>
                  </>
                )}
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {members.map((member) => (
                <div key={member.id} className="flex items-center gap-3">
                  <SignedAvatar
                    src={member.profile?.avatar_url}
                    signedUrl={member.signedUrl}
                    bucket="avatars"
                    className="w-10 h-10"
                    fallback={
                      <span className="bg-muted w-full h-full flex items-center justify-center rounded-full">
                        {member.profile?.full_name?.charAt(0) || member.email?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {member.profile?.full_name || member.email || t('common.user')}
                      {member.user_id === user?.id && ` (${t('common.you')})`}
                    </p>
                    {member.email && (
                      <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleColor(member.role)}`}>
                        {getRoleLabel(member.role, member.user_id)}
                      </span>
                    </div>
                    {member.role === 'caregiver' && id && (
                      <div className="mt-1.5">
                        <CaregiverShiftAssignment
                          patientId={id}
                          memberId={member.id}
                          caregiverName={member.profile?.full_name || member.email || t('common.user')}
                          currentTemplateId={member.shift_template_id}
                          assignmentCounts={shiftAssignmentCounts}
                          canEdit={canAssignShiftTemplates}
                          onUpdate={() => refetchMembers()}
                        />
                        <CareConfigDelegationToggle
                          memberId={member.id}
                          caregiverName={member.profile?.full_name || member.email || t('common.user')}
                          enabled={Boolean((member as any).can_manage_care_config)}
                          canGrant={isOwner || canManageTeam}
                          isPatientActive={isPatientActive}
                          onUpdate={() => refetchMembers()}
                        />
                      </div>
                    )}
                  </div>

                  {/* Remove button: owner can remove others, managers/coordinators can remove caregivers, anyone can leave */}
                  {(
                    (isOwner && member.user_id !== user?.id) ||
                    (canManageTeam && !isOwner && member.role === 'caregiver' && member.user_id !== user?.id) ||
                    (member.user_id === user?.id && !isOwner)
                  ) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleRemoveMember(member)}
                      title={member.user_id === user?.id ? t('patient.leaveTeam') : t('common.remove')}
                    >
                      {member.user_id === user?.id ? (
                        <LogOut className="w-4 h-4" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Company patient: assign caregiver from company + invite observer */}
            {canManageTeam && isCompanyPatient && !isOwner && (
              <div className="mt-4 pt-4 border-t border-border space-y-2">
                <p className="text-sm text-muted-foreground mb-3">{t('patient.inviteToTeam')}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-success/50 text-success hover:bg-success/10 h-auto py-2 whitespace-normal text-center"
                    onClick={() => setShowAssignDialog(true)}
                  >
                    <UserPlus className="w-4 h-4 mr-2 shrink-0" />
                    {t('patient.assignCaregiver')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-muted-foreground/50 text-muted-foreground hover:bg-muted h-auto py-2 whitespace-normal text-center"
                    onClick={() => generateInviteLink('observer')}
                  >
                    <Eye className="w-4 h-4 mr-2 shrink-0" />
                    {t('patient.inviteObserver')}
                  </Button>
                </div>
              </div>
            )}

            {/* Owner: always use invite links */}
            {canInvite && (
              <div className="mt-4 pt-4 border-t border-border space-y-2">
                <p className="text-sm text-muted-foreground mb-3">{t('patient.inviteToTeam')}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-success/50 text-success hover:bg-success/10 h-auto py-2 whitespace-normal text-center"
                    onClick={() => isCompanyPatient ? setShowAssignDialog(true) : generateInviteLink('caregiver')}
                  >
                    <UserPlus className="w-4 h-4 mr-2 shrink-0" />
                    {isCompanyPatient ? t('patient.assignCaregiver') : t('patient.inviteCaregiver')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-muted-foreground/50 text-muted-foreground hover:bg-muted h-auto py-2 whitespace-normal text-center"
                    onClick={() => generateInviteLink('observer')}
                  >
                    <Eye className="w-4 h-4 mr-2 shrink-0" />
                    {t('patient.inviteObserver')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invite Links */}
        {
          inviteLinks.length > 0 && (
            <div className="space-y-3">
              {inviteLinks.map((invite, index) => (
                <Card
                  key={index}
                  className={`${invite.role === 'caregiver'
                    ? 'border-success/50 bg-success/5'
                    : 'border-muted-foreground/30 bg-muted/20'
                    }`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center gap-2 mb-2">
                      {invite.role === 'caregiver' ? (
                        <UserPlus className="w-4 h-4 text-success" />
                      ) : (
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      )}
                      <p className={`text-sm font-medium ${invite.role === 'caregiver' ? 'text-success' : 'text-muted-foreground'
                        }`}>
                        {invite.role === 'caregiver' ? t('roles.caregiver') : t('roles.observer')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-background rounded px-2 py-1 truncate">
                        {invite.link}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyInviteLink(invite.link)}
                      >
                        {invite.copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-[#25D366] hover:text-[#25D366] hover:bg-[#25D366]/10"
                        onClick={() => shareViaWhatsApp(invite.link, invite.role)}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('patient.inviteExpires')}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        }

        {/* Danger zone: archive patient (owner only, when active) */}
        {isOwner && (patient as any).status !== 'archived' && (
          <div className="mt-8 pt-6 border-t border-border">
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">
              {t('patient.archive.dangerZone', { defaultValue: 'Zona de risco' })}
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              {t('patient.archive.dangerZoneHint', {
                defaultValue:
                  'Arquive pacientes que não estão mais sob seus cuidados (alta, transferência, óbito) para liberar slot da assinatura. O histórico permanece acessível.',
              })}
            </p>
            <Button
              variant="outline"
              className="border-destructive/50 text-destructive hover:bg-destructive/10"
              onClick={() => setShowArchiveDialog(true)}
            >
              <Archive className="w-4 h-4 mr-2" />
              {t('patient.archive.action', { defaultValue: 'Arquivar paciente' })}
            </Button>
          </div>
        )}

        {/* Assign Caregiver Dialog */}
        {id && (
          <AssignCompanyCaregiverDialog
            open={showAssignDialog}
            onOpenChange={setShowAssignDialog}
            patientId={id}
            caregivers={availableCaregivers}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['patient-members', id] });
              queryClient.invalidateQueries({ queryKey: ['patient-role', id] });
              refetchCaregivers();
            }}
          />
        )}

        {/* Remove Member Confirmation Dialog */}
        <AlertDialog
          open={!!removeMemberDialog}
          onOpenChange={(open) => {
            if (!open && !isRemovingMember) setRemoveMemberDialog(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{removeMemberDialog?.title}</AlertDialogTitle>
              <AlertDialogDescription>{removeMemberDialog?.description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isRemovingMember}>
                {t('common.cancel', { defaultValue: 'Cancelar' })}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  confirmRemoveMember();
                }}
                disabled={isRemovingMember}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isRemovingMember
                  ? t('common.processing', { defaultValue: 'Processando...' })
                  : removeMemberDialog?.isSelf
                    ? t('patient.leaveTeam', { defaultValue: 'Sair da equipe' })
                    : t('common.remove', { defaultValue: 'Remover' })}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Archive Patient Dialog */}
        {id && patient && (
          <ArchivePatientDialog
            open={showArchiveDialog}
            onOpenChange={setShowArchiveDialog}
            patientId={id}
            patientName={patient.nickname}
            onArchived={() => navigate('/app/patients')}
          />
        )}
      </div >

      {isOwner && (
        <CoachMark
          tourId="patientDetail"
          steps={[
            {
              target: 'patient-shift-templates',
              titleKey: 'tour.patientDetail.templates.title',
              descKey: 'tour.patientDetail.templates.desc',
            },
            {
              target: 'patient-care-plan',
              titleKey: 'tour.patientDetail.carePlan.title',
              descKey: 'tour.patientDetail.carePlan.desc',
            },
            {
              target: 'patient-inventory',
              titleKey: 'tour.patientDetail.inventory.title',
              descKey: 'tour.patientDetail.inventory.desc',
            },
            {
              target: 'patient-team',
              titleKey: 'tour.patientDetail.team.title',
              descKey: 'tour.patientDetail.team.desc',
            },
          ]}
        />
      )}
    </AppLayout >
  );
}